/**
 * Widget 本地存储服务
 *
 * 管理会话、配置和消息的本地持久化
 */

import type { WidgetConfig } from '../config/widget.types';

const STORAGE_PREFIX = 'smart-cs-';

/**
 * Widget 存储服务类
 */
export class WidgetStorage {
  private prefix: string;

  constructor(instanceId?: string) {
    this.prefix = instanceId ? `${STORAGE_PREFIX}${instanceId}-` : STORAGE_PREFIX;
  }

  /**
   * 会话 ID 持久化
   */
  saveConversationId(id: string): void {
    try {
      localStorage.setItem(this.prefix + 'conv-id', id);
    } catch (error) {
      console.warn('[SmartCS] 无法保存会话 ID:', error);
    }
  }

  getConversationId(): string | null {
    try {
      return localStorage.getItem(this.prefix + 'conv-id');
    } catch (error) {
      console.warn('[SmartCS] 无法读取会话 ID:', error);
      return null;
    }
  }

  removeConversationId(): void {
    try {
      localStorage.removeItem(this.prefix + 'conv-id');
    } catch (error) {
      console.warn('[SmartCS] 无法删除会话 ID:', error);
    }
  }

  /**
   * 用户偏好存储
   */
  savePreferences(prefs: Partial<WidgetConfig>): void {
    try {
      localStorage.setItem(this.prefix + 'prefs', JSON.stringify(prefs));
    } catch (error) {
      console.warn('[SmartCS] 无法保存偏好设置:', error);
    }
  }

  getPreferences(): Partial<WidgetConfig> | null {
    try {
      const data = localStorage.getItem(this.prefix + 'prefs');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('[SmartCS] 无法读取偏好设置:', error);
      return null;
    }
  }

  /**
   * 消息历史缓存（会话级）
   */
  saveMessages(messages: any[]): void {
    try {
      sessionStorage.setItem(this.prefix + 'messages', JSON.stringify(messages));
    } catch (error) {
      console.warn('[SmartCS] 无法保存消息缓存:', error);
    }
  }

  getMessages(): any[] | null {
    try {
      const data = sessionStorage.getItem(this.prefix + 'messages');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.warn('[SmartCS] 无法读取消息缓存:', error);
      return null;
    }
  }

  /**
   * 清理所有数据
   */
  clear(): void {
    try {
      // 清理 localStorage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });

      // 清理 sessionStorage
      const sessionKeys = Object.keys(sessionStorage);
      sessionKeys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('[SmartCS] 清理存储时出错:', error);
    }
  }
}

// 全局存储实例
let storageInstance: WidgetStorage | null = null;

/**
 * 获取存储服务实例
 */
export function getWidgetStorage(): WidgetStorage {
  if (!storageInstance) {
    storageInstance = new WidgetStorage();
  }
  return storageInstance;
}
